// components/Map.tsx — 2D top-down world map.
//
// Two surfaces share the same underlying renderer:
//
//   - TrackingMap — a small inset (~200×200) shown at the corner of the
//     game UI. Centred on the player's current room; renders a radius-2
//     neighborhood, the player dot, and any companions in their current
//     room. Click-through to open the FullMap.
//
//   - FullMap — a fullscreen modal triggered by the in-game `map` command
//     (or the inset's hit target). Pannable, wheel-zoomable, touch-friendly.
//     Carries the dimension + level switchers so the player can browse
//     spatial floors and parallel realities independently of where their
//     character currently is.
//
// Inline styles only. No new deps. Pan/zoom/pinch are hand-rolled — the
// SVG content lives inside a `<g>` whose transform is driven by component
// state. Touch events are read directly via `onTouchStart` / `onTouchMove`
// without a third-party gesture library.
//
// Per the engine purity rule, this file is the ONLY place that reads the
// world topology and turns it into UI. engine.ts stays I/O-free.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  type GameState,
  type Npc,
  type Room,
  type RoomShape,
} from "../engine";

// ── Cell geometry ────────────────────────────────────────────────────────
//
// One world cell = CELL_PX × CELL_PX in the SVG's local coordinate space.
// All rendering computes in cell-coords; the outer SVG transform scales and
// pans the whole layer to fit the viewport.

const CELL_PX = 28;
const ROOM_PAD = 3; // px of padding inside each cell for the room rect

// ── Animation constants ──────────────────────────────────────────────────
//
// CSS-transition durations for the organic "stride" movement applied to the
// player dot, companion dots, and the TrackingMap pan. Cross-plane moves
// (dimension or level change) skip the transition entirely — animating
// between parallel realities makes no spatial sense.

const DOT_ANIM_MS = 600;
const PAN_ANIM_MS = 700;
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// ── Path-stepping animation constants ────────────────────────────────────
//
// When a turn's response carries a multi-room movementPath, the player dot
// (and any synced companions + the TrackingMap pan) trace each room in
// sequence. NATURAL_HOP_MS is the per-hop duration at natural pace,
// NATURAL_PAUSE_MS the brief pause between hops. For long paths we scale
// both down so any path fits a PATH_BUDGET_MS total budget — a 5-room walk
// runs at natural pace, a 20-room sprint is a fast trace, every
// intermediate room still flashes the dot through it.

const NATURAL_HOP_MS = 250;
const NATURAL_PAUSE_MS = 50;
const PATH_BUDGET_MS = 2000;

/**
 * Compute per-hop timing for a path of `hopCount` segments (i.e. one less
 * than the number of room ids in the path). Returns the duration each CSS
 * transition should use AND the pause between transitions. When the natural
 * total exceeds PATH_BUDGET_MS, both values scale down proportionally so
 * the whole trace fits inside the budget; otherwise we run at natural pace.
 */
function computePathTiming(hopCount: number): { hopMs: number; pauseMs: number } {
  if (hopCount < 1) return { hopMs: 0, pauseMs: 0 };
  const natural =
    hopCount * NATURAL_HOP_MS + Math.max(0, hopCount - 1) * NATURAL_PAUSE_MS;
  const scale = natural <= PATH_BUDGET_MS ? 1 : PATH_BUDGET_MS / natural;
  return {
    hopMs: NATURAL_HOP_MS * scale,
    pauseMs: NATURAL_PAUSE_MS * scale,
  };
}

/**
 * Single animation driver per Map surface (FullMap, TrackingMap, etc.).
 *
 * Given the player's current room id and an optional ordered movementPath,
 * returns a "displayed step" — the index into the path the dot should
 * currently render at, plus the per-hop transition duration and a
 * skipTransition flag set whenever the next step crosses a plane (dimension
 * or level boundary).
 *
 * The hook owns its own setTimeout-based clock; cleanup on unmount or path
 * change cancels in-flight timers. When `path` is null / empty / length 1,
 * the driver behaves as if no path is animating (returns the current room
 * id, hopMs = DOT_ANIM_MS, skipTransition = false) so callers can pass-
 * through to today's CSS-transition behaviour without branching.
 *
 * Returned `path` is the canonical ordered id list the caller can use to
 * resolve per-step targets (e.g. the panning view's room id at step N).
 */
function usePathAnimation(
  movementPath: string[] | null | undefined,
  currentRoomId: string,
  rooms: GameState["rooms"],
): {
  path: string[];
  stepIndex: number;
  hopMs: number;
  skipTransition: boolean;
  isAnimating: boolean;
} {
  // Normalise the path input. We only treat ≥ 2 entries as an animation
  // trigger — single-hop and missing paths fall through to today's CSS
  // transition (handled by the dot's own DOT_ANIM_MS).
  const normalisedPath = useMemo(() => {
    if (!Array.isArray(movementPath)) return null;
    const filtered = movementPath.filter((id) => typeof id === "string");
    if (filtered.length < 2) return null;
    return filtered;
  }, [movementPath]);

  // Stable identity key for the path — changes when the actual sequence
  // changes. Drives the effect re-fire below.
  const pathKey = normalisedPath ? normalisedPath.join("|") : "";

  // Step 0 = the trace's starting room (which is the player's pre-turn
  // position). The effect below advances this through path.length-1 as
  // timeouts fire. On mount with no path, we sit at 0; the path array
  // there is a single-entry list of state.currentRoom, so step 0 IS the
  // current room.
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [skipTransition, setSkipTransition] = useState<boolean>(false);
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    // Cancel any in-flight timers — a new path supersedes the old.
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current = [];

    if (!normalisedPath) {
      // No animation — make sure we're not stuck on a stale skip flag.
      setStepIndex(0);
      setSkipTransition(false);
      return;
    }

    // Start the walk from step 0 (the player's pre-turn room). Subsequent
    // steps fire on a schedule built from computePathTiming. Each step
    // checks whether it's crossing a plane boundary relative to the
    // previous step's room; if so, we briefly flip skipTransition true
    // so the SVG <g> snaps instead of sliding between realities. After
    // one render frame we restore the flag for the next hop.
    setStepIndex(0);
    setSkipTransition(false);

    const hopCount = normalisedPath.length - 1;
    const { hopMs, pauseMs } = computePathTiming(hopCount);
    const stepDuration = hopMs + pauseMs;

    for (let i = 1; i < normalisedPath.length; i++) {
      const t = setTimeout(() => {
        const fromRoom = rooms[normalisedPath[i - 1]];
        const toRoom = rooms[normalisedPath[i]];
        const fromPlane = fromRoom?.position
          ? `${fromRoom.position.dimension}.${fromRoom.position.level}`
          : null;
        const toPlane = toRoom?.position
          ? `${toRoom.position.dimension}.${toRoom.position.level}`
          : null;
        const crossesPlane =
          fromPlane !== null && toPlane !== null && fromPlane !== toPlane;
        if (crossesPlane) {
          // Snap for this hop. The setStepIndex below will render with
          // transition: none; the post-render frame restores the flag.
          setSkipTransition(true);
          setStepIndex(i);
          // Restore transition on the next macrotask so the FOLLOWING
          // hop animates again.
          const restoreT = setTimeout(() => setSkipTransition(false), 0);
          timersRef.current.push(restoreT);
        } else {
          setSkipTransition(false);
          setStepIndex(i);
        }
      }, i * stepDuration);
      timersRef.current.push(t);
    }

    return () => {
      for (const t of timersRef.current) clearTimeout(t);
      timersRef.current = [];
    };
    // pathKey changes when the actual sequence changes — that's the
    // trigger we want. `rooms` is consulted inside the timer callbacks
    // for plane-boundary detection; we read it through closure on each
    // fire, so we don't need it in the deps array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey]);

  // Compute hopMs FOR THE CURRENT STEP. The same value applies to every
  // step (uniform pacing), so we read it off the path length.
  const hopMs = normalisedPath
    ? computePathTiming(normalisedPath.length - 1).hopMs
    : DOT_ANIM_MS;

  if (!normalisedPath) {
    return {
      path: [currentRoomId],
      stepIndex: 0,
      hopMs: DOT_ANIM_MS,
      skipTransition: false,
      isAnimating: false,
    };
  }

  return {
    path: normalisedPath,
    stepIndex,
    hopMs,
    skipTransition,
    isAnimating: stepIndex < normalisedPath.length - 1,
  };
}

interface MapProps {
  state: GameState;
  scenarioId: string | null;
  /** Companion-candidate NPCs (the typical 3) keyed by id. */
  npcs?: Record<string, Npc>;
  /** Called when the user closes the modal (FullMap only). */
  onClose?: () => void;
  /**
   * Optional handler for the "Pop out" button in the FullMap header. When
   * absent the button is hidden — used by MapWindow (the pop-out itself
   * shouldn't offer to pop out again) and by callers that don't want the
   * affordance. The main-window modal in App.tsx supplies a handler that
   * opens `/?map=<storyId>` and closes the modal.
   */
  onPopOut?: () => void;
  /**
   * When true the FullMap renders as a full-viewport surface with no modal
   * scrim and no surrounding shell — suitable for MapWindow (the popped-out
   * second-monitor view). The header still renders; the close button calls
   * the supplied `onClose` (which MapWindow wires to `window.close()`).
   */
  embedded?: boolean;
  /**
   * Optional ordered list of room ids the player traversed THIS turn.
   * When present and length ≥ 2, the map step-animates the player dot,
   * companion dots, and the panning view through each room in sequence
   * (see commit landing path-stepping animation). Absent / null falls
   * through to today's single-hop CSS-transition animation.
   *
   * Validated by the App.tsx caller against the post-delta state before
   * it ever reaches a Map component — see handleSubmit.
   */
  movementPath?: string[] | null;
}

// ── Public components ────────────────────────────────────────────────────

/**
 * Small map inset. Renders inside the existing game-side-pane layout.
 * Always centred on the current room. Clicking the inset (or the "expand"
 * link) is up to the caller — the inset itself does not own the modal.
 */
export function TrackingMap({
  state,
  npcs,
  onOpen,
  movementPath,
}: {
  state: GameState;
  /** Currently unused — reserved for future scenario-aware inset styling. */
  scenarioId?: string | null;
  npcs?: Record<string, Npc>;
  onOpen?: () => void;
  /**
   * Optional multi-room transit path (see MapProps.movementPath). When
   * present, the inset's panning view step-animates through each room
   * in sequence and the companion dots ride along.
   */
  movementPath?: string[] | null;
}) {
  const { t } = useTranslation("game");
  // Inset is locked to the player's actual D.L — no switchers, no panning.
  const dimension = state.dimension ?? 0;
  const level = state.level ?? 0;

  // Path-stepping driver — owns the clock that advances the displayed
  // player room id along the trace. When idle (no movementPath), this
  // resolves to state.currentRoom and the pan animates a single hop via
  // its default PAN_ANIM_MS CSS transition.
  const playerPathAnim = usePathAnimation(
    movementPath,
    state.currentRoom,
    state.rooms,
  );
  const displayedPlayerRoomId =
    playerPathAnim.path[playerPathAnim.stepIndex] ?? state.currentRoom;
  const cur = state.rooms[displayedPlayerRoomId] ?? state.rooms[state.currentRoom];
  const pos = cur?.position ?? { x: 0, y: 0, dimension, level };

  // 5×5 cells centred on player. The viewBox is fixed at this size; the
  // inner content <g> translates so the player's world-cell maps to the
  // inset centre, and that translation is what CSS-transitions for the
  // pan animation.
  const radius = 2;
  const sizeCells = radius * 2 + 1;
  const innerSize = sizeCells * CELL_PX;
  const centerX = innerSize / 2;
  const centerY = innerSize / 2;

  // World-space translation that re-centres the player at (centerX, centerY).
  const mapTx = centerX - (pos.x + 0.5) * CELL_PX;
  const mapTy = centerY - (pos.y + 0.5) * CELL_PX;

  // Cross-plane jumps (dimension OR level change) skip the pan transition:
  // animating between parallel realities makes no spatial sense. Combine
  // the legacy auto-detect (the dot/pos's plane changed across renders)
  // with the path driver's per-hop snap signal — either triggers a snap.
  const currentPlane = `${pos.dimension ?? dimension}.${pos.level ?? level}`;
  const prevPlaneRef = useRef(currentPlane);
  const skipPanTransition =
    prevPlaneRef.current !== currentPlane || playerPathAnim.skipTransition;
  useEffect(() => {
    prevPlaneRef.current = currentPlane;
  });

  // Per-hop transition duration. Idle → PAN_ANIM_MS (legacy). Mid-trace →
  // the scaled hop duration the driver computed for this path. We keep the
  // PAN-vs-DOT slight differential at natural pace by using the driver's
  // hopMs directly — short hops should match the dot, not lag behind.
  const panMs = playerPathAnim.isAnimating
    ? playerPathAnim.hopMs
    : PAN_ANIM_MS;

  // Render a slightly wider bbox so cells around the panning area are
  // pre-rendered — otherwise the slide reveals empty edges.
  const bboxRadius = radius + 1;
  const bbox = {
    minX: pos.x - bboxRadius,
    minY: pos.y - bboxRadius,
    maxX: pos.x + bboxRadius,
    maxY: pos.y + bboxRadius,
  };

  return (
    <div
      style={trackingWrap}
      onClick={onOpen}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      title={onOpen ? t("map.openFullMap") : undefined}
    >
      <div style={trackingTitle}>{t("map.title")}</div>
      <svg
        viewBox={`0 0 ${innerSize} ${innerSize}`}
        width={170}
        height={170}
        style={{ display: "block" }}
      >
        {/* Inner content panned underneath the player. The translate is
            set as a CSS transform (not the SVG `transform` attribute) so
            CSS transitions actually animate it — Safari + some Chrome
            versions don't animate attribute changes via CSS transition.
            transform-box: fill-box keeps the SVG-local coord system. */}
        <g
          style={{
            transform: `translate(${mapTx}px, ${mapTy}px)`,
            transformBox: "fill-box",
            transformOrigin: "0 0",
            transition: skipPanTransition
              ? "none"
              : `transform ${panMs}ms ${EASE}`,
          }}
        >
          <MaskLayer
            state={state}
            dimension={dimension}
            level={level}
            bbox={bbox}
          />
          <RoomsLayer
            state={state}
            dimension={dimension}
            level={level}
            bbox={bbox}
          />
          {/* Companion dots ride along with the panning map — they live
              inside the translated <g>. animated=false keeps the dots'
              own transforms instant; the panning <g> above slides under
              them so they appear to move with the world. */}
          <CompanionDots
            state={state}
            dimension={dimension}
            level={level}
            npcs={npcs}
            animated={false}
          />
        </g>
        {/* Player dot rendered OUTSIDE the panning group, pinned at the
            inset centre. Never animates — the map slides under it. */}
        <PlayerDotFixed cx={centerX} cy={centerY} />
      </svg>
      {onOpen && <div style={trackingHint}>{t("map.tapToExpand")}</div>}
    </div>
  );
}

/**
 * Fullscreen modal. Pannable + zoomable + pinch-friendly. Renders dimension
 * + level <select> pickers when more than one of each axis exists in the
 * known rooms; hidden entirely when there's nothing to switch to.
 */
export function FullMap({
  state,
  npcs,
  onClose,
  onPopOut,
  embedded = false,
  movementPath,
}: MapProps) {
  const { t } = useTranslation("game");
  const playerDim = state.dimension ?? 0;
  const playerLevel = state.level ?? 0;

  // Independent map-view D.L — the user can browse other dimensions / levels
  // without affecting where the character actually is.
  const [viewDim, setViewDim] = useState<number>(playerDim);
  const [viewLevel, setViewLevel] = useState<number>(playerLevel);

  // Hovered/tapped room id — drives the room-name tooltip. Hover sets +
  // clears via mouseenter/mouseleave; touch tap toggles (tap same room
  // again to dismiss). pointer-events:none on the label itself so it
  // doesn't intercept further events.
  const [hoveredRoomId, setHoveredRoomId] = useState<string | null>(null);

  // Build a sorted set of dimensions and levels that contain at least one
  // room. The switchers' arrows are enabled / disabled based on this.
  const { dimensions, levelsByDim } = useMemo(() => {
    const dims = new Set<number>();
    const lvls = new Map<number, Set<number>>();
    for (const r of Object.values(state.rooms)) {
      const d = r.position?.dimension ?? 0;
      const l = r.position?.level ?? 0;
      dims.add(d);
      if (!lvls.has(d)) lvls.set(d, new Set());
      lvls.get(d)!.add(l);
    }
    dims.add(playerDim); // ensure the player's home is selectable
    if (!lvls.has(playerDim)) lvls.set(playerDim, new Set());
    lvls.get(playerDim)!.add(playerLevel);
    const dimensions = Array.from(dims).sort((a, b) => a - b);
    const levelsByDim: Record<number, number[]> = {};
    for (const [d, set] of lvls) {
      levelsByDim[d] = Array.from(set).sort((a, b) => a - b);
    }
    return { dimensions, levelsByDim };
  }, [state.rooms, playerDim, playerLevel]);

  // Whenever the viewDim changes, snap viewLevel onto the closest known
  // level in that dimension (avoids showing an empty floor).
  useEffect(() => {
    const known = levelsByDim[viewDim] ?? [0];
    if (!known.includes(viewLevel)) {
      // Pick the closest available level.
      let best = known[0];
      for (const lvl of known) {
        if (Math.abs(lvl - viewLevel) < Math.abs(best - viewLevel)) {
          best = lvl;
        }
      }
      setViewLevel(best);
    }
  }, [viewDim, levelsByDim, viewLevel]);

  // Only render a switcher when there's actually something to switch to.
  // Previously these were also rendered when the scenario's semantics
  // declared the axis (spatial → always level, dimensional → always dim),
  // but with a single D.L known the stepper arrows compute as both
  // disabled and clicks appear to do nothing. Hide them entirely until
  // there's a second option — the empty stepper is worse than no stepper.
  const showDimensionSwitcher = dimensions.length > 1;
  const showLevelSwitcher = (levelsByDim[viewDim]?.length ?? 1) > 1;

  // ── Pan / zoom state ───────────────────────────────────────────────────
  // Scale is in viewport-px-per-cell. tx/ty are viewport-px offsets applied
  // to the SVG root <g> transform.
  const [scale, setScale] = useState<number>(20); // 20 px / cell ≈ comfy
  const [tx, setTx] = useState<number>(0);
  const [ty, setTy] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // On first mount / when the player's D.L changes, recentre on the player.
  const recentreOnPlayer = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cur = state.rooms[state.currentRoom];
    const pos = cur?.position ?? { x: 0, y: 0 };
    // Centre the player's cell in the viewport.
    setTx(rect.width / 2 - (pos.x + 0.5) * scale);
    setTy(rect.height / 2 - (pos.y + 0.5) * scale);
  }, [scale, state.rooms, state.currentRoom]);

  useEffect(() => {
    // Run once after mount (containerRef populated). Re-run if the player
    // moves or the view scales meaningfully.
    recentreOnPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wheel: zoom around the cursor. Attached as a NATIVE non-passive
  // listener (not via React's onWheel prop) because React binds wheel
  // listeners passively by default — preventDefault() is then silently
  // ignored, which lets the browser pinch-zoom the whole page on Mac
  // trackpads instead of zooming the map. addEventListener with
  // { passive: false } restores preventDefault.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      // Mac trackpad pinch-zoom surfaces as wheel + ctrlKey=true with small
      // deltaY values. Use a smooth exponential factor so it feels
      // continuous instead of stepping in 1.1× chunks. Regular mouse-wheel
      // scroll keeps the original discrete step.
      const factor = e.ctrlKey
        ? Math.exp(-e.deltaY * 0.01)
        : e.deltaY < 0
          ? 1.1
          : 1 / 1.1;
      setScale((s) => {
        const next = Math.max(6, Math.min(72, s * factor));
        const ratio = next / s;
        setTx((curTx) => cx - (cx - curTx) * ratio);
        setTy((curTy) => cy - (cy - curTy) * ratio);
        return next;
      });
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, []);

  // Pointer-drag pan. Uses pointer events to coalesce mouse + pen.
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      dragRef.current = { x: e.clientX, y: e.clientY };
    },
    [],
  );
  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.x;
      const dy = e.clientY - dragRef.current.y;
      dragRef.current = { x: e.clientX, y: e.clientY };
      setTx((t) => t + dx);
      setTy((t) => t + dy);
    },
    [],
  );
  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    },
    [],
  );

  // Touch pinch + drag. We track up to two fingers; one finger pans, two
  // finger pinches around their midpoint.
  const touchRef = useRef<{
    last: Array<{ id: number; x: number; y: number }>;
    pinchStartDist: number;
    pinchStartScale: number;
  } | null>(null);
  const handleTouchStart = useCallback(
    (e: ReactTouchEvent<HTMLDivElement>) => {
      const touches = Array.from(e.touches).slice(0, 2).map((t) => ({
        id: t.identifier,
        x: t.clientX,
        y: t.clientY,
      }));
      if (touches.length === 2) {
        const dx = touches[0].x - touches[1].x;
        const dy = touches[0].y - touches[1].y;
        touchRef.current = {
          last: touches,
          pinchStartDist: Math.hypot(dx, dy) || 1,
          pinchStartScale: scale,
        };
      } else {
        touchRef.current = {
          last: touches,
          pinchStartDist: 0,
          pinchStartScale: scale,
        };
      }
    },
    [scale],
  );
  const handleTouchMove = useCallback(
    (e: ReactTouchEvent<HTMLDivElement>) => {
      const ref = touchRef.current;
      if (!ref) return;
      e.preventDefault();
      const touches = Array.from(e.touches).slice(0, 2).map((t) => ({
        id: t.identifier,
        x: t.clientX,
        y: t.clientY,
      }));
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (touches.length === 2 && ref.last.length === 2) {
        const dx = touches[0].x - touches[1].x;
        const dy = touches[0].y - touches[1].y;
        const dist = Math.hypot(dx, dy) || 1;
        const ratio = dist / ref.pinchStartDist;
        const nextScale = Math.max(
          6,
          Math.min(72, ref.pinchStartScale * ratio),
        );
        const mx = (touches[0].x + touches[1].x) / 2 - rect.left;
        const my = (touches[0].y + touches[1].y) / 2 - rect.top;
        setScale((s) => {
          const r = nextScale / s;
          setTx((curTx) => mx - (mx - curTx) * r);
          setTy((curTy) => my - (my - curTy) * r);
          return nextScale;
        });
      } else if (touches.length === 1 && ref.last.length >= 1) {
        // Match to the SAME finger if we can; otherwise just use the first.
        const prev =
          ref.last.find((t) => t.id === touches[0].id) ?? ref.last[0];
        const dx = touches[0].x - prev.x;
        const dy = touches[0].y - prev.y;
        setTx((t) => t + dx);
        setTy((t) => t + dy);
      }
      ref.last = touches;
    },
    [],
  );
  const handleTouchEnd = useCallback(() => {
    touchRef.current = null;
  }, []);

  // ── Bounding box: enough to fit every visible room (the view's D + L). ─
  const bbox = useMemo(() => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const r of Object.values(state.rooms)) {
      const p = r.position;
      if (!p) continue;
      if (p.dimension !== viewDim || p.level !== viewLevel) continue;
      const span = typeof r.cellSpan === "number" ? r.cellSpan : 1;
      minX = Math.min(minX, p.x - 1);
      minY = Math.min(minY, p.y - 1);
      maxX = Math.max(maxX, p.x + span);
      maxY = Math.max(maxY, p.y + 1);
    }
    if (!Number.isFinite(minX)) {
      minX = -2;
      minY = -2;
      maxX = 2;
      maxY = 2;
    }
    return { minX, minY, maxX, maxY };
  }, [state.rooms, viewDim, viewLevel]);

  // Visible empty state — the view contains no rooms.
  const hasRooms = useMemo(() => {
    for (const r of Object.values(state.rooms)) {
      const p = r.position;
      if (!p) continue;
      if (p.dimension === viewDim && p.level === viewLevel) return true;
    }
    return false;
  }, [state.rooms, viewDim, viewLevel]);

  const viewingPlayer = viewDim === playerDim && viewLevel === playerLevel;

  // Path-stepping animation driver. When a multi-room movementPath arrives,
  // this hook owns the clock that advances the dot through each room in
  // sequence; otherwise it sits idle and the dot animates a single hop via
  // its default DOT_ANIM_MS CSS transition. The clock is local to FullMap —
  // pop-out and inline insets each own their own (the inset is TrackingMap).
  const playerPathAnim = usePathAnimation(
    movementPath,
    state.currentRoom,
    state.rooms,
  );
  // The dot's *displayed* room id at the current step. When idle, this is
  // just state.currentRoom; mid-trace it advances along the path. Position
  // falls back to the player's actual room if the step's id doesn't
  // resolve (shouldn't happen post-validation, but defensive).
  const displayedPlayerRoomId =
    playerPathAnim.path[playerPathAnim.stepIndex] ?? state.currentRoom;
  const displayedPlayerRoom = state.rooms[displayedPlayerRoomId];
  const displayedPlayerPos = displayedPlayerRoom?.position ?? {
    x: 0,
    y: 0,
    dimension: viewDim,
    level: viewLevel,
  };

  // Picker option lists. The <select> handles its own enabled/disabled
  // logic — it's just a list of values per axis.
  const currentLevels = levelsByDim[viewDim] ?? [viewLevel];

  // Outer wrapper varies by mode:
  //   - default (in-game modal): full-viewport scrim with a centred shell
  //     and a click-outside-to-close handler.
  //   - embedded (MapWindow pop-out): no scrim, the FullMap fills the
  //     window directly so the second monitor doesn't paint a fake modal
  //     on its own surface.
  const wrapperStyle: CSSProperties = embedded ? embeddedWrap : modalScrim;
  const shellStyle: CSSProperties = embedded ? embeddedShell : modalShell;
  return (
    <div
      role={embedded ? undefined : "dialog"}
      aria-modal={embedded ? undefined : true}
      style={wrapperStyle}
      onClick={
        embedded
          ? undefined
          : (e) => {
              if (e.target === e.currentTarget) onClose?.();
            }
      }
    >
      <div style={shellStyle}>
        <div style={modalHeader}>
          <div style={modalTitle}>
            {t("map.title")}
            {!viewingPlayer && (
              <button
                type="button"
                onClick={() => {
                  setViewDim(playerDim);
                  setViewLevel(playerLevel);
                }}
                style={backToPlayerBtn}
              >
                {t("map.backToPlayer")}
              </button>
            )}
          </div>
          <div style={switcherRow}>
            {showDimensionSwitcher && (
              <Picker
                label={t("map.dimension")}
                ariaLabel={t("map.selectAria", { label: t("map.dimension") })}
                value={viewDim}
                options={dimensions}
                onChange={(v) => setViewDim(v)}
              />
            )}
            {showLevelSwitcher && (
              <Picker
                label={t("map.level")}
                ariaLabel={t("map.selectAria", { label: t("map.level") })}
                value={viewLevel}
                options={currentLevels}
                onChange={(v) => setViewLevel(v)}
              />
            )}
            <button
              type="button"
              onClick={recentreOnPlayer}
              style={recentreBtn}
              title={t("map.recentreTitle")}
            >
              {t("map.recentre")}
            </button>
            {onPopOut && (
              <button
                type="button"
                onClick={onPopOut}
                style={popoutBtn}
                title={t("map.popoutTitle")}
              >
                ↗ {t("map.popoutLabel")}
              </button>
            )}
            <button
              type="button"
              onClick={() => onClose?.()}
              style={closeBtn}
              aria-label={t("map.closeAria")}
            >
              ×
            </button>
          </div>
        </div>
        <div
          ref={containerRef}
          style={canvasWrap}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          {!hasRooms ? (
            <div style={emptyState}>
              <div style={emptyTitle}>{t("map.emptyTitle")}</div>
              <div style={emptyBody}>
                {viewingPlayer
                  ? t("map.emptyExplore")
                  : t("map.emptyOtherDL")}
              </div>
            </div>
          ) : (
            <svg
              width="100%"
              height="100%"
              style={{ display: "block", cursor: dragRef.current ? "grabbing" : "grab" }}
            >
              <g transform={`translate(${tx} ${ty}) scale(${scale / CELL_PX})`}>
                <MaskLayer
                  state={state}
                  dimension={viewDim}
                  level={viewLevel}
                  bbox={bbox}
                />
                <RoomsLayer
                  state={state}
                  dimension={viewDim}
                  level={viewLevel}
                  bbox={bbox}
                  hoveredRoomId={hoveredRoomId}
                  setHoveredRoomId={setHoveredRoomId}
                />
                {viewingPlayer && (
                  <PlayerDot
                    pos={displayedPlayerPos}
                    hopMs={playerPathAnim.hopMs}
                    skipTransition={playerPathAnim.skipTransition}
                  />
                )}
                <CompanionDots
                  state={state}
                  dimension={viewDim}
                  level={viewLevel}
                  npcs={npcs}
                  animated
                  playerPath={
                    playerPathAnim.isAnimating ? playerPathAnim.path : undefined
                  }
                  playerStepIndex={playerPathAnim.stepIndex}
                  playerHopMs={playerPathAnim.hopMs}
                  playerSkipTransition={playerPathAnim.skipTransition}
                />
              </g>
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Switcher widget ──────────────────────────────────────────────────────

function Picker({
  label,
  ariaLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  /** Pre-composed aria-label so the parent can run it through t(). */
  ariaLabel: string;
  value: number;
  options: number[];
  onChange: (next: number) => void;
}) {
  return (
    <div style={switcher}>
      <label style={switcherLabel} htmlFor={`map-picker-${label}`}>
        {label}
      </label>
      <select
        id={`map-picker-${label}`}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        style={pickerSelect}
        aria-label={ariaLabel}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Inner SVG layers ─────────────────────────────────────────────────────

interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function MaskLayer({
  state,
  dimension,
  level,
  bbox,
}: {
  state: GameState;
  dimension: number;
  level: number;
  bbox: BBox;
}) {
  // Render blocked cells in the bbox as faint grey hatching marks.
  const tiles: JSX.Element[] = [];
  for (const [key, value] of Object.entries(state.worldMask ?? {})) {
    if (value !== "blocked") continue;
    const [xs, ys, ds, ls] = key.split(",");
    const x = Number(xs);
    const y = Number(ys);
    const d = Number(ds);
    const l = Number(ls);
    if (d !== dimension || l !== level) continue;
    if (
      x < bbox.minX - 1 ||
      x > bbox.maxX + 1 ||
      y < bbox.minY - 1 ||
      y > bbox.maxY + 1
    )
      continue;
    tiles.push(
      <rect
        key={key}
        x={x * CELL_PX + ROOM_PAD}
        y={y * CELL_PX + ROOM_PAD}
        width={CELL_PX - ROOM_PAD * 2}
        height={CELL_PX - ROOM_PAD * 2}
        fill="rgba(120,110,100,0.10)"
        stroke="rgba(120,110,100,0.18)"
        strokeWidth={0.5}
        strokeDasharray="2 2"
      />,
    );
  }
  return <g>{tiles}</g>;
}

function RoomsLayer({
  state,
  dimension,
  level,
  bbox,
  hoveredRoomId,
  setHoveredRoomId,
}: {
  state: GameState;
  dimension: number;
  level: number;
  bbox: BBox;
  hoveredRoomId?: string | null;
  setHoveredRoomId?: (id: string | null) => void;
}) {
  const rooms = Object.values(state.rooms).filter((r) => {
    const p = r.position;
    if (!p) return false;
    if (p.dimension !== dimension || p.level !== level) return false;
    if (p.x < bbox.minX - 2 || p.x > bbox.maxX + 2) return false;
    if (p.y < bbox.minY - 2 || p.y > bbox.maxY + 2) return false;
    return true;
  });

  const hoveredRoom = hoveredRoomId
    ? rooms.find((r) => r.id === hoveredRoomId)
    : null;

  return (
    <g>
      {/* exits first so they sit beneath the cells */}
      {rooms.map((r) => (
        <RoomExits key={`exits-${r.id}`} room={r} state={state} />
      ))}
      {rooms.map((r) => (
        <RoomCell
          key={r.id}
          room={r}
          isCurrent={r.id === state.currentRoom}
          onHoverChange={setHoveredRoomId}
          hoveredRoomId={hoveredRoomId}
        />
      ))}
      {/* Tooltip — rendered LAST so it sits above other rooms. Empty when
          nothing is hovered. */}
      {hoveredRoom && hoveredRoom.position && (
        <RoomNameTooltip room={hoveredRoom} />
      )}
    </g>
  );
}

/**
 * Floating room-name label drawn above the hovered/tapped cell. Lives inside
 * the SVG transform so it follows pan/zoom automatically. Uses stroke +
 * paint-order so the text reads against any underlying map content without
 * needing a separate background rect (less DOM churn, no measurement step).
 */
function RoomNameTooltip({ room }: { room: Room }) {
  const p = room.position!;
  // Anchor at the centre top of the room's bounding cells. For multi-cell
  // rooms (cellSpan > 1) we centre across the whole footprint.
  const cells = room.cells ?? [`${p.x},${p.y}`];
  const xs = cells.map((c) => Number(c.split(",")[0]));
  const ys = cells.map((c) => Number(c.split(",")[1]));
  const minCx = Math.min(...xs);
  const maxCx = Math.max(...xs);
  const minCy = Math.min(...ys);
  const centreX = ((minCx + maxCx + 1) / 2) * CELL_PX;
  // Sit a few px above the top edge of the cell. Negative dy in SVG goes up.
  const labelY = minCy * CELL_PX - 4;
  return (
    <text
      x={centreX}
      y={labelY}
      textAnchor="middle"
      style={{
        fontFamily: "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
        fontSize: 13,
        fontStyle: "italic",
        fontWeight: 500,
        fill: "var(--ink, #e8d9b8)",
        stroke: "rgba(8, 6, 4, 0.95)",
        strokeWidth: 4,
        paintOrder: "stroke fill",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {room.name || room.id}
    </text>
  );
}

function RoomCell({
  room,
  isCurrent,
  onHoverChange,
  hoveredRoomId,
}: {
  room: Room;
  isCurrent: boolean;
  onHoverChange?: (id: string | null) => void;
  hoveredRoomId?: string | null;
}) {
  const p = room.position!;
  const shape: RoomShape = (room.shape as RoomShape) ?? "rect";
  const span = typeof room.cellSpan === "number" ? room.cellSpan : 1;
  const fill = isCurrent
    ? "var(--accent, #d4a574)"
    : room.visited
      ? "rgba(180,150,110,0.35)"
      : "rgba(180,150,110,0.18)";
  const stroke = isCurrent
    ? "var(--accent, #d4a574)"
    : "rgba(212,165,116,0.55)";

  const cells = room.cells ?? [`${p.x},${p.y}`];

  // Hover / tap handlers. mouseEnter / mouseLeave drive desktop hover with
  // zero browser delay. onClick toggles for touch — tap same room again to
  // dismiss the label, tap another room to switch. We don't set hover on
  // touch via pointerover because a pan-drag also fires pointerover; click
  // fires only on a discrete tap (the browser suppresses click during a
  // drag-gesture).
  const isHovered = hoveredRoomId === room.id;
  const handlers = onHoverChange
    ? {
        onMouseEnter: () => onHoverChange(room.id),
        onMouseLeave: () => onHoverChange(null),
        onClick: () => onHoverChange(isHovered ? null : room.id),
        style: { cursor: "default" } as CSSProperties,
      }
    : {};

  if (shape === "tunnel" && span > 1) {
    // Single capsule that covers all the cells.
    const xs = cells.map((c) => Number(c.split(",")[0]));
    const ys = cells.map((c) => Number(c.split(",")[1]));
    const minXc = Math.min(...xs);
    const maxXc = Math.max(...xs);
    const minYc = Math.min(...ys);
    const maxYc = Math.max(...ys);
    const x = minXc * CELL_PX + ROOM_PAD;
    const y = minYc * CELL_PX + ROOM_PAD;
    const w = (maxXc - minXc + 1) * CELL_PX - ROOM_PAD * 2;
    const h = (maxYc - minYc + 1) * CELL_PX - ROOM_PAD * 2;
    const r = Math.min(w, h) / 2;
    return (
      <g {...handlers}>
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={r}
          ry={r}
          fill={fill}
          stroke={stroke}
          strokeWidth={isCurrent ? 1.6 : 1}
        />
        <ExitGlyphs room={room} />
      </g>
    );
  }

  if (shape === "cave") {
    return (
      <g {...handlers}>
        {cells.map((c) => {
          const [cxs, cys] = c.split(",");
          const cx = Number(cxs);
          const cy = Number(cys);
          return (
            <path
              key={c}
              d={cavePath(cx, cy, room.id)}
              fill={fill}
              stroke={stroke}
              strokeWidth={isCurrent ? 1.6 : 1}
            />
          );
        })}
        <ExitGlyphs room={room} />
      </g>
    );
  }

  // Default rect (and any unknown shape).
  return (
    <g {...handlers}>
      {cells.map((c) => {
        const [cxs, cys] = c.split(",");
        const cx = Number(cxs);
        const cy = Number(cys);
        return (
          <rect
            key={c}
            x={cx * CELL_PX + ROOM_PAD}
            y={cy * CELL_PX + ROOM_PAD}
            width={CELL_PX - ROOM_PAD * 2}
            height={CELL_PX - ROOM_PAD * 2}
            rx={2}
            ry={2}
            fill={fill}
            stroke={stroke}
            strokeWidth={isCurrent ? 1.6 : 1}
          />
        );
      })}
      <ExitGlyphs room={room} />
    </g>
  );
}

/**
 * Overlay glyphs showing which directions a room exits in. Cardinal exits
 * (N/S/E/W) render as short notches on the corresponding edge of the cell
 * furthest in that direction (so a multi-cell tunnel's east exit lives at
 * its rightmost cell, not its anchor). Vertical exits (up/down) render as
 * small triangles in the top-left / bottom-left of the anchor cell so they
 * don't fight for the cardinal-notch territory.
 *
 * Renders for every known room — both visited and merely glimpsed — so the
 * player can read connectivity from the map without having to enter.
 */
function ExitGlyphs({ room }: { room: Room }) {
  const exits = (room.exits ?? {}) as Record<string, string | null | undefined>;
  const rawCells = room.cells ?? (room.position
    ? [`${room.position.x},${room.position.y}`]
    : []);
  if (rawCells.length === 0) return null;

  const cellsXY = rawCells.map((c) => {
    const [xs, ys] = c.split(",");
    return { x: Number(xs), y: Number(ys) };
  });
  const anchorCell = cellsXY[0]!;

  // Pick the cell furthest in each cardinal direction so multi-cell rooms
  // annotate the right edge cell, not the anchor (which may sit interior).
  const pickCell = (
    pred: (a: { x: number; y: number }, b: { x: number; y: number }) => boolean,
  ) => cellsXY.reduce((best, cur) => (pred(cur, best) ? cur : best), cellsXY[0]!);

  const color = "var(--accent, #d4a574)";
  const cellInner = CELL_PX - ROOM_PAD * 2;
  const notchLen = Math.max(4, cellInner * 0.4);
  const notchOffset = ROOM_PAD; // sit the notch right on the room edge

  const cellCenter = (c: { x: number; y: number }) => ({
    cx: c.x * CELL_PX + CELL_PX / 2,
    cy: c.y * CELL_PX + CELL_PX / 2,
  });

  const elements: JSX.Element[] = [];

  if (exits.north) {
    const { cx, cy } = cellCenter(pickCell((a, b) => a.y < b.y));
    const edgeY = cy - CELL_PX / 2 + notchOffset;
    elements.push(
      <line
        key="n"
        x1={cx - notchLen / 2}
        y1={edgeY}
        x2={cx + notchLen / 2}
        y2={edgeY}
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
      />,
    );
  }
  if (exits.south) {
    const { cx, cy } = cellCenter(pickCell((a, b) => a.y > b.y));
    const edgeY = cy + CELL_PX / 2 - notchOffset;
    elements.push(
      <line
        key="s"
        x1={cx - notchLen / 2}
        y1={edgeY}
        x2={cx + notchLen / 2}
        y2={edgeY}
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
      />,
    );
  }
  if (exits.east) {
    const { cx, cy } = cellCenter(pickCell((a, b) => a.x > b.x));
    const edgeX = cx + CELL_PX / 2 - notchOffset;
    elements.push(
      <line
        key="e"
        x1={edgeX}
        y1={cy - notchLen / 2}
        x2={edgeX}
        y2={cy + notchLen / 2}
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
      />,
    );
  }
  if (exits.west) {
    const { cx, cy } = cellCenter(pickCell((a, b) => a.x < b.x));
    const edgeX = cx - CELL_PX / 2 + notchOffset;
    elements.push(
      <line
        key="w"
        x1={edgeX}
        y1={cy - notchLen / 2}
        x2={edgeX}
        y2={cy + notchLen / 2}
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
      />,
    );
  }

  // Vertical exits — small filled triangles in the anchor cell's corners.
  // Up points upward in the top-left; down points downward in the bottom-left.
  if (exits.up || exits.down) {
    const { cx, cy } = cellCenter(anchorCell);
    const arrowSize = Math.max(4, cellInner * 0.25);
    const inset = ROOM_PAD + 1.5;
    if (exits.up) {
      const ax = cx - CELL_PX / 2 + inset;
      const ay = cy - CELL_PX / 2 + inset;
      elements.push(
        <polygon
          key="u"
          points={`${ax},${ay + arrowSize} ${ax + arrowSize},${ay + arrowSize} ${ax + arrowSize / 2},${ay}`}
          fill={color}
        />,
      );
    }
    if (exits.down) {
      const ax = cx - CELL_PX / 2 + inset;
      const ay = cy + CELL_PX / 2 - inset;
      elements.push(
        <polygon
          key="d"
          points={`${ax},${ay - arrowSize} ${ax + arrowSize},${ay - arrowSize} ${ax + arrowSize / 2},${ay}`}
          fill={color}
        />,
      );
    }
  }

  return <>{elements}</>;
}

// Cave-shape generator: a closed path of 8 vertices on a wobbled circle,
// deterministic per room id so the same cave doesn't reshape on re-render.
function cavePath(cx: number, cy: number, roomId: string): string {
  const centreX = cx * CELL_PX + CELL_PX / 2;
  const centreY = cy * CELL_PX + CELL_PX / 2;
  const baseR = CELL_PX / 2 - ROOM_PAD;
  // Mix a small per-id offset so neighbouring caves don't all match.
  let seed = 0;
  for (let i = 0; i < roomId.length; i++) {
    seed = (seed * 31 + roomId.charCodeAt(i)) >>> 0;
  }
  const verts = 8;
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < verts; i++) {
    const angle = (i / verts) * Math.PI * 2;
    seed = (Math.imul(seed, 1103515245) + 12345) >>> 0;
    const jitter = ((seed & 0xff) / 255) * 0.35 + 0.75; // 0.75..1.10
    const r = baseR * jitter;
    pts.push([centreX + Math.cos(angle) * r, centreY + Math.sin(angle) * r]);
  }
  return (
    "M " +
    pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join(" L ") +
    " Z"
  );
}

// Render exits for one room. Open / unexplored = dotted nub; existing = solid
// line to the connected room's centre. Vertical exits (up/down) get a glyph
// in the corner of the cell rather than a line.
function RoomExits({ room, state }: { room: Room; state: GameState }) {
  const p = room.position;
  if (!p) return null;
  const lines: JSX.Element[] = [];
  const cellCentre = (cx: number, cy: number) => ({
    x: cx * CELL_PX + CELL_PX / 2,
    y: cy * CELL_PX + CELL_PX / 2,
  });
  const me = cellCentre(p.x, p.y);
  for (const [dir, target] of Object.entries(room.exits)) {
    if (dir === "up" || dir === "down") {
      if (!target) continue;
      // Vertical portal glyph in the cell's corner.
      const cx = me.x + (dir === "up" ? CELL_PX / 3 : CELL_PX / 4);
      const cy = me.y + (dir === "up" ? -CELL_PX / 3 : CELL_PX / 3);
      lines.push(
        <text
          key={`${room.id}-${dir}`}
          x={cx}
          y={cy}
          fontSize={CELL_PX * 0.35}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--accent, #d4a574)"
        >
          {dir === "up" ? "↑" : "↓"}
        </text>,
      );
      continue;
    }
    const offset = (() => {
      switch (dir) {
        case "north":
          return { x: 0, y: -CELL_PX };
        case "south":
          return { x: 0, y: CELL_PX };
        case "east":
          return { x: CELL_PX, y: 0 };
        case "west":
          return { x: -CELL_PX, y: 0 };
        default:
          return null;
      }
    })();
    if (!offset) continue;
    if (target && state.rooms[target]?.position) {
      const other = state.rooms[target].position!;
      // Only draw lines for exits on the same D.L; vertical exits already
      // handled above.
      if (other.dimension !== p.dimension || other.level !== p.level) continue;
      const otherCentre = cellCentre(other.x, other.y);
      lines.push(
        <line
          key={`${room.id}-${dir}`}
          x1={me.x}
          y1={me.y}
          x2={otherCentre.x}
          y2={otherCentre.y}
          stroke="rgba(212,165,116,0.65)"
          strokeWidth={1.2}
        />,
      );
    } else {
      // Open / unexplored exit — short dotted nub off the cell edge.
      lines.push(
        <line
          key={`${room.id}-${dir}-open`}
          x1={me.x}
          y1={me.y}
          x2={me.x + offset.x * 0.4}
          y2={me.y + offset.y * 0.4}
          stroke="rgba(212,165,116,0.35)"
          strokeWidth={1}
          strokeDasharray="2 2"
        />,
      );
    }
  }
  return <g>{lines}</g>;
}

/**
 * Animated player dot — used by FullMap. Wraps the circle in a
 * <g transform="translate(...)"> whose transform CSS-transitions over
 * `hopMs` (default DOT_ANIM_MS). Cross-plane jumps (dimension or level
 * change) flip the transition to "none" for that single render so the dot
 * snaps to the new position; a useEffect updates the prev-plane ref
 * afterwards so subsequent renders animate normally again.
 *
 * When the parent passes a path-stepping driver (`hopMs` + `skipTransition`
 * + a per-step `pos`), this component honours those overrides instead of
 * deriving its own — the driver owns the clock and we just render. The
 * internal plane-ref stays useful for the case where there is NO path
 * driver and the dot animates a single hop across a plane boundary.
 */
function PlayerDot({
  pos,
  hopMs,
  skipTransition: externalSkipTransition,
}: {
  pos: { x: number; y: number; dimension?: number; level?: number };
  /**
   * Optional override for the CSS transition duration. When omitted, falls
   * back to DOT_ANIM_MS (the single-hop default). The path-animation hook
   * supplies a scaled value derived from the trace's hopCount.
   */
  hopMs?: number;
  /**
   * Optional override for the "snap, don't transition" flag. When omitted,
   * the component computes it from its own previous-plane ref (the legacy
   * single-hop cross-plane skip). When supplied, the external driver wins —
   * useful when the driver is mid-trace and wants to snap at a known
   * boundary hop inside the path.
   */
  skipTransition?: boolean;
}) {
  const cx = (pos.x + 0.5) * CELL_PX;
  const cy = (pos.y + 0.5) * CELL_PX;

  const currentPlane = `${pos.dimension ?? 0}.${pos.level ?? 0}`;
  const prevPlaneRef = useRef(currentPlane);
  const internalSkipTransition = prevPlaneRef.current !== currentPlane;
  useEffect(() => {
    prevPlaneRef.current = currentPlane;
  });

  const effectiveSkip =
    externalSkipTransition !== undefined
      ? externalSkipTransition || internalSkipTransition
      : internalSkipTransition;
  const effectiveMs = typeof hopMs === "number" ? hopMs : DOT_ANIM_MS;

  return (
    <g
      style={{
        // CSS-property transform (not the SVG attribute) so the CSS
        // transition can actually animate the move. Safari + several
        // Chrome versions don't transition SVG attribute changes
        // reliably. transform-box: fill-box pins the origin to the
        // element's bounding box so translate values are in user units.
        transform: `translate(${cx}px, ${cy}px)`,
        transformBox: "fill-box",
        transformOrigin: "0 0",
        transition: effectiveSkip
          ? "none"
          : `transform ${effectiveMs}ms ${EASE}`,
      }}
    >
      <circle
        cx={0}
        cy={0}
        r={CELL_PX * 0.2}
        fill="var(--accent, #d4a574)"
        stroke="#0d0907"
        strokeWidth={1}
      />
    </g>
  );
}

/**
 * Fixed-position player dot for TrackingMap. Never animates — it sits at
 * the inset centre while the map content pans underneath it.
 */
function PlayerDotFixed({ cx, cy }: { cx: number; cy: number }) {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={CELL_PX * 0.2}
      fill="var(--accent, #d4a574)"
      stroke="#0d0907"
      strokeWidth={1}
    />
  );
}

/**
 * Renders the visible companion-candidate NPCs on the current D.L. Each
 * NPC dot is its own component instance so it can own its own previous-
 * plane ref and animate independently of the others (and of the player).
 *
 * `animated` is true on FullMap (each dot has its own translate + CSS
 * transition). On TrackingMap the dots live inside the panning <g>, so
 * their own transforms don't transition — they ride along with the pan.
 *
 * When the parent passes `playerPath` (the player's movementPath for the
 * turn) + `playerStepIndex` (driven by usePathAnimation), each companion
 * whose pre- and post-turn room both appear on the path in order will
 * animate through its sub-segment in sync with the player. Otherwise the
 * dot single-hops via its own CSS-transition.
 */
function CompanionDots({
  state,
  dimension,
  level,
  npcs,
  animated = true,
  playerPath,
  playerStepIndex,
  playerHopMs,
  playerSkipTransition,
}: {
  state: GameState;
  dimension: number;
  level: number;
  npcs?: Record<string, Npc>;
  animated?: boolean;
  /** Player's movementPath this turn (ordered room ids). */
  playerPath?: string[];
  /** Current step index along playerPath, owned by the driver hook. */
  playerStepIndex?: number;
  /** Per-hop transition duration the driver hook chose for this trace. */
  playerHopMs?: number;
  /** Whether the current hop should snap (cross-plane). */
  playerSkipTransition?: boolean;
}) {
  const source = npcs ?? state.npcs ?? {};
  const entries: Array<{
    npc: Npc;
    p: { x: number; y: number; dimension: number; level: number };
    index: number;
    currentRoomId: string;
  }> = [];
  let i = 0;
  for (const npc of Object.values(source)) {
    if (!npc) continue;
    // Resolve the room the NPC is in. "with_player" piggybacks on the
    // player's current room.
    let roomId: string | null = null;
    if (npc.location === "with_player") {
      roomId = state.currentRoom;
    } else if (
      typeof npc.location === "string" &&
      npc.location.startsWith("room:")
    ) {
      roomId = npc.location.slice(5);
    }
    if (!roomId) {
      i += 1;
      continue;
    }
    const room = state.rooms[roomId];
    const p = room?.position;
    if (!p) {
      i += 1;
      continue;
    }
    if (p.dimension !== dimension || p.level !== level) {
      i += 1;
      continue;
    }
    entries.push({ npc, p, index: i, currentRoomId: roomId });
    i += 1;
  }
  return (
    <g>
      {entries.map(({ npc, p, index, currentRoomId }) => (
        <CompanionDot
          key={npc.id}
          npc={npc}
          pos={p}
          index={index}
          animated={animated}
          rooms={state.rooms}
          currentRoomId={currentRoomId}
          playerPath={playerPath}
          playerStepIndex={playerStepIndex}
          playerHopMs={playerHopMs}
          playerSkipTransition={playerSkipTransition}
          dimension={dimension}
          level={level}
        />
      ))}
    </g>
  );
}

/**
 * One companion dot. Owns its own previous-plane ref + previous-room ref
 * so it animates (or snaps) independently of the player and the other
 * companions. Each dot is offset slightly in its cell so multiple NPCs in
 * the same room with the player don't stack on one pixel.
 *
 * Path-following: when both the companion's PREVIOUS room id and its
 * CURRENT room id appear on the player's `playerPath` in order, the dot
 * rides through the player's path between those two indices. The walk is
 * synced to `playerStepIndex` — at any given step, the companion sits at
 * the room corresponding to its progress along its own sub-segment. NPCs
 * not on the path single-hop via their own CSS transition (today's
 * behaviour). NPCs that didn't move stay put.
 */
function CompanionDot({
  npc,
  pos,
  index,
  animated,
  rooms,
  currentRoomId,
  playerPath,
  playerStepIndex,
  playerHopMs,
  playerSkipTransition,
  dimension,
  level,
}: {
  npc: Npc;
  pos: { x: number; y: number; dimension: number; level: number };
  index: number;
  animated: boolean;
  rooms: GameState["rooms"];
  currentRoomId: string;
  playerPath?: string[];
  playerStepIndex?: number;
  playerHopMs?: number;
  playerSkipTransition?: boolean;
  dimension: number;
  level: number;
}) {
  // Track the companion's previous room id across renders so we can tell
  // whether they moved this turn AND whether their pre+post positions
  // both lie on the player's path.
  const prevRoomRef = useRef<string>(currentRoomId);
  // Snapshot the previous room BEFORE useEffect's commit phase so the
  // render that follows a move can still see the old id. We update the
  // ref in an effect AFTER the render commits.
  const prevRoomId = prevRoomRef.current;
  useEffect(() => {
    prevRoomRef.current = currentRoomId;
  });

  // Path-following decision. Both prev and current must be on the path,
  // and prev must come BEFORE current (correct order). NPCs that didn't
  // move (prev === current) skip path-following entirely — they stay put.
  let displayedPos = pos;
  let pathHopMs: number | undefined = undefined;
  let pathSkip = false;
  let ridingPath = false;
  if (
    Array.isArray(playerPath) &&
    playerPath.length >= 2 &&
    typeof playerStepIndex === "number" &&
    prevRoomId !== currentRoomId
  ) {
    const prevIdx = playerPath.indexOf(prevRoomId);
    const currIdx = playerPath.indexOf(currentRoomId);
    if (prevIdx >= 0 && currIdx > prevIdx) {
      // Companion's sub-segment is [prevIdx..currIdx]. At any player
      // step S, the companion sits at min(S, currIdx) but never before
      // prevIdx. Clamps keep the dot inside its sub-segment.
      const subIdx = Math.min(
        currIdx,
        Math.max(prevIdx, playerStepIndex ?? prevIdx),
      );
      const useRoom = rooms[playerPath[subIdx]];
      const usePos = useRoom?.position;
      if (usePos && usePos.dimension === dimension && usePos.level === level) {
        displayedPos = usePos;
        pathHopMs =
          typeof playerHopMs === "number" ? playerHopMs : DOT_ANIM_MS;
        pathSkip = Boolean(playerSkipTransition);
        ridingPath = true;
      }
    }
  }

  const angle = (index * Math.PI * 2) / 3;
  const r = CELL_PX * 0.18;
  const cx = (displayedPos.x + 0.5) * CELL_PX + Math.cos(angle) * 4;
  const cy = (displayedPos.y + 0.5) * CELL_PX + Math.sin(angle) * 4;

  const currentPlane = `${displayedPos.dimension}.${displayedPos.level}`;
  const prevPlaneRef = useRef(currentPlane);
  const internalSkipTransition = prevPlaneRef.current !== currentPlane;
  useEffect(() => {
    prevPlaneRef.current = currentPlane;
  });

  const effectiveSkip = ridingPath
    ? pathSkip || internalSkipTransition
    : internalSkipTransition;
  const effectiveMs =
    ridingPath && typeof pathHopMs === "number" ? pathHopMs : DOT_ANIM_MS;

  return (
    <g
      style={{
        transform: `translate(${cx}px, ${cy}px)`,
        transformBox: "fill-box",
        transformOrigin: "0 0",
        transition:
          !animated || effectiveSkip
            ? "none"
            : `transform ${effectiveMs}ms ${EASE}`,
      }}
    >
      <circle
        cx={0}
        cy={0}
        r={r * 0.65}
        fill="#9ac6d8"
        stroke="#0d0907"
        strokeWidth={0.8}
      >
        <title>{npc.name}</title>
      </circle>
    </g>
  );
}

// ── Inline styles ────────────────────────────────────────────────────────

const trackingWrap: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.55))",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  borderRadius: 6,
  padding: 10,
  width: 200,
  fontFamily: "var(--font-body, serif)",
  color: "var(--ink, #e8d9b8)",
  cursor: "pointer",
};

const trackingTitle: CSSProperties = {
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--ink-muted, #8a7355)",
  marginBottom: 6,
};

const trackingHint: CSSProperties = {
  fontSize: "0.65rem",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--ink-muted, #8a7355)",
  marginTop: 6,
  textAlign: "center",
};

const modalScrim: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9000,
};

const modalShell: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.95))",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.28))",
  borderRadius: 8,
  width: "min(900px, 92vw)",
  height: "min(720px, 88vh)",
  display: "flex",
  flexDirection: "column",
  color: "var(--ink, #e8d9b8)",
  fontFamily: "var(--font-body, serif)",
};

// Embedded variant — used by MapWindow (the popped-out second-monitor view).
// No scrim, no centring; the FullMap fills the window. The ScenarioTheme-
// Wrapper above us is responsible for painting the page background, so we
// stay transparent here.
const embeddedWrap: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "stretch",
  justifyContent: "stretch",
  background: "var(--bg-gradient, transparent)",
  zIndex: 0,
};

const embeddedShell: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.95))",
  border: "none",
  borderRadius: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  color: "var(--ink, #e8d9b8)",
  fontFamily: "var(--font-body, serif)",
};

const modalHeader: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 14px",
  borderBottom: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  gap: 12,
  flexWrap: "wrap",
};

const modalTitle: CSSProperties = {
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.14em",
  color: "var(--ink-muted, #8a7355)",
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const backToPlayerBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.4))",
  color: "var(--accent, #d4a574)",
  padding: "2px 8px",
  borderRadius: 3,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  cursor: "pointer",
  fontFamily: "inherit",
};

const switcherRow: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
};

const switcher: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
  padding: "0 6px",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  borderRadius: 4,
};

const switcherLabel: CSSProperties = {
  fontSize: "0.65rem",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--ink-muted, #8a7355)",
  marginRight: 4,
};

// Native <select> in the dimension/level picker. Inherits font from
// the modal, paints accent text on transparent background to match the
// arrow-stepper aesthetic the picker replaced.
const pickerSelect: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--ink, #e8d9b8)",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  padding: "2px 4px",
  cursor: "pointer",
  fontVariantNumeric: "tabular-nums",
  appearance: "auto",
};

const recentreBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "0.65rem",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  padding: "3px 8px",
  borderRadius: 3,
  cursor: "pointer",
  fontFamily: "inherit",
};

// "Pop out" button on FullMap's header. Slightly more prominent than the
// recentre control (accent border) so the affordance stands out — but not
// so prominent it competes with the close X.
const popoutBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.4))",
  color: "var(--accent, #d4a574)",
  fontSize: "0.65rem",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  padding: "3px 8px",
  borderRadius: 3,
  cursor: "pointer",
  fontFamily: "inherit",
};

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--ink, #e8d9b8)",
  fontSize: "1.4rem",
  lineHeight: 1,
  cursor: "pointer",
  padding: "0 6px",
  fontFamily: "inherit",
};

const canvasWrap: CSSProperties = {
  flex: 1,
  overflow: "hidden",
  touchAction: "none",
  position: "relative",
  background: "var(--bg-gradient, transparent)",
};

const emptyState: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--ink-muted, #8a7355)",
  textAlign: "center",
  padding: 24,
};

const emptyTitle: CSSProperties = {
  fontSize: "0.95rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
};

const emptyBody: CSSProperties = {
  fontSize: "0.8rem",
  fontStyle: "italic",
};
